/**
 * Takes an input datetime and if it is in on of the following formats:
 *   1. YYYY:MM:DD HH:MM:SS.SSS+hh:mm or YYYY:MM:DD HH:MM:SS.SSS-hh:mm
 *   2. YYYY:MM:DD HH:MM:SS or YYYY:MM:DD HH:mm:ss.SSS
 * then returns the string formatted as "YYYY:MM:DD HH:MM:SS.SSS+hh:mm", and otherwise returns undefined.
 */
const validateAndUpdateDateTimeString = (dateTimeString: string): string | undefined => {
  /*
    Regular expression to match the date-time format "YYYY:MM:DD HH:MM:SS.SSS+hh:mm".
    Example: 2019:12:31 15:15:13+00:00, 2019:12:31 15:15:13-00:00, 2019:12:31 15:15:13.000-00:00 and
             2019:12:31 15:15:13.000+00:00
  */
  const validDateTimeWithOffsetRegex = /\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}(\.\d{3})?[+-]\d{2}:\d{2}/g;

  /*
    Regular expression to match the date-time format "YYYY:MM:DD HH:MM:SS" or "YYYY:MM:DD HH:mm:ss.SSS".
    Example: 2019:12:31 15:15:13.034, 2019:12:31 15:15:13
  */
  const validDateTimeRegex = /^\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}(\.\d{3})?$/;
  if (validDateTimeWithOffsetRegex.exec(dateTimeString)) return dateTimeString;
  if (validDateTimeRegex.exec(dateTimeString)) {
    // Added offset to the date time to simplify the conversion.
    dateTimeString += '+00:00';
    return dateTimeString;
  }
  return undefined;
};


/**
 * Converts string containing a date-time in one of the formats supported by "validateAndUpdateDateTimeString" into an ISO string format.
 * Throws a TypeError if the input date-time string is invalid.
 * @param {string} dateTimeString - A string containing a date-time in one of the formats supported by "validateAndUpdateDateTimeString"
 * @returns {string} Returns the date-time string converted to ISO string format.
 * @throws {TypeError} Throws TypeError for unsupported date-time string formats.
 */
export const convertDateTimetoIsoString = (dateTimeString: string): string => {
  const dateTime = validateAndUpdateDateTimeString(dateTimeString);
  if (!dateTime) {
    throw new TypeError(`Invalid date time provided. Provided date-time: ${dateTimeString}`);
  }

  const [date, time] = dateTime.split(' ');
  const dateWithNewSeparator = date?.replace(/:/g, '-');
  return new Date(`${dateWithNewSeparator} ${time}`).toISOString();
};


/**
 * Takes an input datetime and and validate if the date is in YYYY-MM-DDTHH:MM:SSZ format.
 * return true if the input date time is in the specified format (for example: 2016-08-11T09:16:29.000Z)
 */
export const isIsoDateTime = (dateTimeString: string): boolean => {
  const validIsoRegex: RegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
  return validIsoRegex.test(dateTimeString);
};
